import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PropertyRequestFields from "../components/propertyRequest/PropertyRequestFields";
import ContactFields from "../components/propertyRequest/ContactFields";
import { formatDate } from "../components/propertyRequest/labels";
import { fromRequest, toPayload, validateContact, validateCriteria } from "../utils/propertyRequestForm";
import { propertyRequestApi } from "../Services/propertyRequestService";

// Only active requests are editable and an account has at most 10 of them: one page of "mine" holds it.
export default function PropertyRequestEdit() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: "", request: null });
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [contactErrors, setContactErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    let current = true;
    propertyRequestApi.mine("active", 0, 50)
      .then((page) => {
        if (!current) return;
        const request = (page?.items || []).find((item) => item.id === id) || null;
        setState({ loading: false, error: request ? "" : t("propertyRequest.edit.notEditable"), request });
        if (request) setForm(fromRequest(request));
      })
      .catch((error) => { if (current) setState({ loading: false, error: error.message, request: null }); });
    return () => { current = false; };
  }, [id, t]);

  const save = async (event) => {
    event.preventDefault();
    const found = validateCriteria(form.criteria, form.text);
    const contactFound = validateContact(form.contact);
    setErrors(found); setContactErrors(contactFound);
    if (Object.keys(found).length || Object.keys(contactFound).length || saving) return;
    setSaving(true); setApiError("");
    try {
      // ExpiresAt is not part of the body: the server keeps the original expiry.
      await propertyRequestApi.update(id, toPayload(form.criteria, form.text, form.contact));
      toast.success(t("propertyRequest.edit.saved"));
      navigate("/my-property-requests");
    } catch (error) {
      setApiError(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="lina-property-request mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
      <Link to="/my-property-requests" className="text-sm font-semibold text-green-800 underline">{t("propertyRequest.edit.back")}</Link>
      <h1 className="mt-3 text-3xl font-black text-slate-900 sm:text-4xl">{t("propertyRequest.edit.title")}</h1>
      {state.loading && <p role="status" className="mt-6 text-slate-600">{t("propertyRequest.mine.loading")}</p>}
      {state.error && <p role="alert" className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">{state.error}</p>}
      {form && state.request && (
        <form className="mt-4 space-y-5" noValidate onSubmit={save}>
          <p className="text-sm text-slate-600">{t("propertyRequest.edit.expiryNote", { date: formatDate(state.request.expiresAt, i18n.language) })}</p>
          <PropertyRequestFields text={form.text} onTextChange={(text) => setForm({ ...form, text })}
            criteria={form.criteria} onCriteriaChange={(criteria) => setForm({ ...form, criteria })} errors={errors} />
          <ContactFields contact={form.contact} onChange={(contact) => setForm({ ...form, contact })} errors={contactErrors} />
          {(Object.keys(errors).length > 0 || Object.keys(contactErrors).length > 0) && <p role="alert" className="text-sm font-semibold text-red-700">{t("propertyRequest.errors.fixBelow")}</p>}
          {apiError && <p role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">{apiError}</p>}
          <button type="submit" className="lina-action-primary h-12 w-full text-base" disabled={saving}>{saving ? t("propertyRequest.edit.saving") : t("propertyRequest.edit.save")}</button>
        </form>
      )}
    </main>
  );
}
